import { Box, Skeleton, Text, VStack } from '@chakra-ui/react';
import type React from 'react';

/** How the status line is coloured. Copy always names the state too, colour is never the only signal. */
export type MigrationTone = 'neutral' | 'pending' | 'error' | 'success';

const TONE_COLOR: Record<MigrationTone, string> = {
  neutral: 'fg.muted',
  pending: 'orange.fg',
  error: 'red.fg',
  success: 'green.fg',
};

interface MigrationStateLayoutProps {
  tone: MigrationTone;
  /** The one sentence naming the current situation. */
  status: string;
  /** Secondary sentence: a date or a reason. Rendered only when the state has one. */
  detail?: string;
  /** Information-only line shown when the store sells in another currency. */
  currencyNotice?: string;
  /** The admin call to action, or the sentence a viewer reads instead. */
  children?: React.ReactNode;
}

/**
 * Body of the success-fee migration card.
 *
 * The status and detail sentences sit in a polite live region so a Pusher
 * activation or the return-from-Shopify refetch is announced without stealing
 * focus. The action sits outside it, so it is not re-announced.
 */
export const MigrationStateLayout: React.FC<MigrationStateLayoutProps> = ({
  tone,
  status,
  detail,
  currencyNotice,
  children,
}) => (
  <VStack align="flex-start" gap={3}>
    <Box aria-live="polite" aria-atomic="true">
      <VStack align="flex-start" gap={1}>
        <Text fontSize="sm" color={TONE_COLOR[tone]} fontWeight="medium">
          {status}
        </Text>
        {detail && (
          <Text fontSize="sm" color="fg.muted">
            {detail}
          </Text>
        )}
        {currencyNotice && (
          <Text fontSize="sm" color="fg.muted">
            {currencyNotice}
          </Text>
        )}
      </VStack>
    </Box>
    {children}
  </VStack>
);

/**
 * Placeholder shown while the status is refetched after the Shopify approval
 * page, so the stale pre-return state never flashes.
 */
export const MigrationStateLoading: React.FC<{ label: string }> = ({ label }) => (
  <Box aria-live="polite" aria-atomic="true">
    <VStack align="flex-start" gap={2}>
      <Text fontSize="sm" color="fg.muted">
        {label}
      </Text>
      <Skeleton height="18px" width="260px" />
      <Skeleton height="18px" width="180px" />
    </VStack>
  </Box>
);
